/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslatePipe } from '@ngx-translate/core';
import { PersonRef, WorkflowService } from './workflow.service';

export interface WorkflowDialogData {
  mode: 'reassign' | 'comment' | 'pickPerson' | 'pickGroup';
  title: string;
}

@Component({
  selector: 'app-workflow-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, TranslatePipe],
  template: `
    <h2 mat-dialog-title>{{ data.title | translate }}</h2>
    <mat-dialog-content>
      <ng-container *ngIf="data.mode === 'comment'; else picker">
        <mat-form-field appearance="outline" style="width: 420px; max-width: 100%">
          <mat-label>{{ 'DMS.WF_DIALOG.COMMENT' | translate }}</mat-label>
          <textarea matInput rows="4" [(ngModel)]="comment"></textarea>
        </mat-form-field>
      </ng-container>

      <ng-template #picker>
        <mat-form-field appearance="outline" style="width: 420px; max-width: 100%">
          <mat-label>{{ (data.mode === 'pickGroup' ? 'DMS.WF_DIALOG.SEARCH_GROUP' : 'DMS.WF_DIALOG.SEARCH_PERSON') | translate }}</mat-label>
          <input
            matInput
            [(ngModel)]="term"
            (ngModelChange)="search($event)"
            [matAutocomplete]="auto"
            [placeholder]="'DMS.WF_DIALOG.TYPE_TO_SEARCH' | translate"
          />
          <mat-autocomplete #auto="matAutocomplete" (optionSelected)="pick($event.option.value)">
            <mat-option *ngFor="let r of results()" [value]="r">{{ r.displayName }}</mat-option>
          </mat-autocomplete>
        </mat-form-field>
        <p class="aca-wf-dialog__hint" *ngIf="selected()">
          {{ 'DMS.WF_DIALOG.SELECTED' | translate }} <strong>{{ selected()?.displayName }}</strong>
        </p>
      </ng-template>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="close()">{{ 'DMS.WF_DIALOG.CANCEL' | translate }}</button>
      <button mat-flat-button color="primary" (click)="confirm()" [disabled]="!isValid()">
        {{ 'DMS.WF_DIALOG.CONFIRM' | translate }}
      </button>
    </mat-dialog-actions>
  `,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .aca-wf-dialog__hint {
        font-size: 13px;
        color: var(--theme-secondary-text, rgba(0, 0, 0, 0.6));
      }
    `
  ]
})
export class WorkflowDialogComponent {
  readonly data = inject<WorkflowDialogData>(MAT_DIALOG_DATA);
  private readonly ref = inject(MatDialogRef<WorkflowDialogComponent>);
  private readonly wf = inject(WorkflowService);

  term = '';
  comment = '';
  readonly results = signal<PersonRef[]>([]);
  readonly selected = signal<PersonRef | null>(null);

  search(term: string): void {
    const obs = this.data.mode === 'pickGroup' ? this.wf.searchGroups(term) : this.wf.searchPeople(term);
    obs.subscribe((r) => this.results.set(r));
  }

  pick(person: PersonRef): void {
    this.selected.set(person);
    this.term = person.displayName;
  }

  isValid(): boolean {
    return this.data.mode === 'comment' ? this.comment.trim().length > 0 : !!this.selected();
  }

  confirm(): void {
    this.ref.close(this.data.mode === 'comment' ? this.comment.trim() : this.selected());
  }

  close(): void {
    this.ref.close();
  }
}
